import {
  CardContainer,
  LabeledDateValue,
  LabeledSelectValue,
  LabeledSpecializedValue,
  TextWithBreaks,
  TruncatedTextValue,
  useScreenWidth,
} from "@msys/ui";
import {
  CalendarToday as CalendarTodayIcon,
  Euro as EuroIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { LabeledContactValue } from "../../../commons/labeled-values/LabeledContactValue.js";
import { LabeledAddressValue } from "../../addresses/LabeledAddressValue.js";
import { CategoriesLabeledValue } from "../../categories/CategoriesLabeledValue.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { RequestOverviewHeaderBoxFragment } from "./RequestOverviewHeaderBox.generated.js";

export const RequestOverviewHeaderBox = ({
  request,
}: {
  request: RequestOverviewHeaderBoxFragment;
}) => {
  const { t } = useTranslate(["RequestOverview", "Global"]);

  const { isMinTablet } = useScreenWidth();

  const { projectTypeOptions: opportunityTypeOptions } = useProjectTypes();

  return (
    <CardContainer>
      <Stack
        direction="column"
        spacing={isMinTablet ? 2 : 1}
        sx={{
          p: isMinTablet ? 2 : 1,
        }}
      >
        <Stack direction="column" spacing={1}>
          <Typography variant="h2">
            {t("Quote request", {
              ns: "RequestOverview",
            })}
          </Typography>
          {request.instructions && (
            <Typography>
              <TextWithBreaks text={request.instructions} />
            </Typography>
          )}
        </Stack>

        <Divider />

        {request.description && (
          <Box>
            <TruncatedTextValue
              text={request.description}
              lines={3}
              showMoreLabel={t("Show more", { ns: "Global" })}
              showLessLabel={t("Show less", { ns: "Global" })}
              notSetLabel={t("Not set", { ns: "Global" })}
            />
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 6,
              md: 4,
              xl: 3,
            }}
          >
            <CategoriesLabeledValue
              label={t("Categories", { ns: "RequestOverview" })}
              categoryIds={request.categories}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              md: 4,
              xl: 3,
            }}
          >
            <LabeledSelectValue
              label={t("Project type", {
                ns: "RequestOverview",
              })}
              options={opportunityTypeOptions}
              value={request.type}
              notSetText={t("Not set", {
                ns: "Global",
              })}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              md: 4,
              xl: 3,
            }}
          >
            <LabeledAddressValue
              label={t("Project location", {
                ns: "RequestOverview",
              })}
              address={request.address}
              variant="body2"
              icon={LocationOnIcon}
              hideStreetLines={!request.marketplaceFullAddress}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              md: 4,
              xl: 3,
            }}
          >
            <LabeledContactValue
              label={t("Client", {
                ns: "RequestOverview",
              })}
              contact={request.requestorDoc.contractee}
              icon={PersonIcon}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              md: 4,
              xl: 3,
            }}
          >
            <LabeledSpecializedValue
              label={t("Budget", {
                ns: "RequestOverview",
              })}
              value={request.budget}
              type="money"
              icon={EuroIcon}
              notSetText={t("Not set", {
                ns: "Global",
              })}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              md: 4,
              xl: 3,
            }}
          >
            <LabeledDateValue
              label={t("Earliest project start", {
                ns: "RequestOverview",
              })}
              value={request.earliestStart}
              icon={CalendarTodayIcon}
              notSetText={t("Not set", {
                ns: "Global",
              })}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              md: 4,
              xl: 3,
            }}
          >
            <LabeledDateValue
              label={t("Work deadline", {
                ns: "RequestOverview",
              })}
              value={request.deadline}
              icon={CalendarTodayIcon}
              notSetText={t("Not set", {
                ns: "Global",
              })}
            />
          </Grid>
        </Grid>
      </Stack>
    </CardContainer>
  );
};
